import { useEffect, useState } from 'react';
import { MapContainer, TileLayer, CircleMarker, GeoJSON, useMap } from 'react-leaflet';
import type { LatLngBoundsExpression } from 'leaflet';
import type { GeoJsonObject } from 'geojson';
import clsx from 'clsx';
import { getCountryGeo, normalizeLongitude } from '../../utils/countryGeo';

/**
 * Metropolitan France including Corsica: Pointe de Corsen (-5.14) to Cap Corse (9.56), and
 * Bray-Dunes (51.09) down to Bonifacio (41.33). Corsica is inside the box so that no French
 * venue's pin can fall outside the frame; it costs only a little Mediterranean in the corner.
 */
const FRANCE_BOUNDS: LatLngBoundsExpression = [
  [41.3, -5.2],
  [51.1, 9.7],
];
// Starting view only — fitBounds refines it once the container is measured.
const FRANCE_CENTER: [number, number] = [46.6, 2.2];
const FRANCE_ZOOM = 3;

/**
 * Country borders, drawn over the tiles. At these zooms the raster tiles' own borders are
 * hairline-thin and all but vanish under the dark theme's dimming filter, which leaves the
 * frames unreadable — a pin in an unbroken green expanse. Stroked linework is legible at 128px.
 *
 * It is ~134 KB (44 KB over the wire), so it is loaded on demand rather than bundled into the
 * entry chunk: the module resolves once per session and every later map reuses it. The layer is
 * decoration — if the import fails the map renders exactly as it did before.
 * Regenerate with `scripts/build-country-borders.js`.
 */
let bordersCache: GeoJsonObject | null = null;
let bordersRequest: Promise<void> | null = null;

function useCountryBorders(): GeoJsonObject | null {
  const [borders, setBorders] = useState<GeoJsonObject | null>(bordersCache);
  useEffect(() => {
    if (bordersCache) return;
    let active = true;
    bordersRequest ??= import('../../assets/countryBorders.json')
      .then(m => { bordersCache = m.default as unknown as GeoJsonObject; })
      .catch(() => { bordersRequest = null; }); // retry on the next map; never surfaced
    bordersRequest.then(() => { if (active && bordersCache) setBorders(bordersCache); });
    return () => { active = false; };
  }, []);
  return borders;
}

interface VenueMiniMapProps {
  latitude?: number;
  longitude?: number;
  /** The venue's country. Anything but France reframes the square on that country's continent. */
  country?: string;
  /** Used for the tooltip and the accessible label, e.g. "Lyon, France". */
  label?: string;
  /** Square edge in px. */
  size?: number;
  className?: string;
}

/**
 * The frame is always a whole country or continent — the point of the square is to show *where*
 * a venue sits, which a street-level view cannot convey. MapContainer reads `center`/`zoom`
 * only at creation, so the fit is applied here, after the container has been measured.
 */
function FitBounds({ bounds }: { bounds: LatLngBoundsExpression }) {
  const map = useMap();
  useEffect(() => {
    const fit = () => {
      map.invalidateSize();
      map.fitBounds(bounds, { padding: [2, 2], animate: false });
    };
    fit();
    // The overlay animates in, so the container may still be mid-layout on the first pass.
    // Re-fit next frame: invalidateSize alone would keep a zoom derived from a stale size.
    const frame = requestAnimationFrame(fit);
    return () => cancelAnimationFrame(frame);
  }, [map, bounds]);
  return null;
}

/**
 * Small static OpenStreetMap thumbnail of a venue.
 *
 * In France — the only country the BAN geocoder covers, so the only one with a real pin — the
 * frame is the whole country and the pin is the venue's commune. Elsewhere the frame is the
 * country's continent and the pin is the country itself: coordinates that precise don't exist
 * for foreign venues, but "somewhere in Germany" still beats a grey square. With neither (no
 * coordinates and no recognisable country) it greys out.
 *
 * It never captures drag or scroll — it lives inside a scrollable overlay.
 */
export function VenueMiniMap({ latitude, longitude, country, label, size = 128, className }: VenueMiniMapProps) {
  const hasCoords = typeof latitude === 'number' && typeof longitude === 'number';
  const borders = useCountryBorders();
  const countryGeo = getCountryGeo(country);
  // France keeps its own tighter frame; the country table's FR entry is only a pin.
  const foreign = countryGeo && countryGeo.code !== 'FR' ? countryGeo : undefined;
  const bounds = foreign ? foreign.bounds : FRANCE_BOUNDS;

  // Venue coordinates win when they exist — a foreign venue can still carry them, from an
  // import or an admin edit. The country pin is the fallback, and is drawn softer to say so.
  const pin = hasCoords
    ? { latitude, longitude, approximate: false }
    : foreign
      ? { latitude: foreign.latitude, longitude: foreign.longitude, approximate: true }
      : null;

  const countryName = foreign?.name;
  const linkZoom = pin?.approximate ? 5 : 12;
  const linkTitle = pin?.approximate
    ? `${countryName} — approximate country location, open in OpenStreetMap`
    : label
      ? `${label} — open in OpenStreetMap`
      : 'Open in OpenStreetMap';

  const map = (
    <div
      className={clsx(
        'venue-mini-map relative rounded-md overflow-hidden border border-dark-border bg-dark-elevated',
        !pin && 'grayscale opacity-30'
      )}
      style={{ width: size, height: size }}
    >
      <MapContainer
        center={FRANCE_CENTER}
        zoom={FRANCE_ZOOM}
        className="h-full w-full"
        style={{ pointerEvents: 'none' }}
        // Fractional zoom: France fits a 128px square at zoom 3.59, so snapping to integer 3
        // would leave it filling only two thirds of the frame.
        zoomSnap={0}
        zoomControl={false}
        attributionControl={false}
        dragging={false}
        scrollWheelZoom={false}
        doubleClickZoom={false}
        touchZoom={false}
        boxZoom={false}
        keyboard={false}
      >
        <TileLayer url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" maxZoom={19} />
        {borders && (
          <GeoJSON
            data={borders}
            // Stroke only: a fill would hide the tiles underneath. The colour lives in index.css
            // so it can follow the theme — the dark themes dim the tiles but not this pane.
            style={{ className: 'venue-mini-map-borders', weight: 0.6, fill: false, interactive: false }}
          />
        )}
        <FitBounds bounds={bounds} />
        {pin && (
          <CircleMarker
            // Oceania's frame runs past the antimeridian, where a raw negative longitude would
            // project a world away from the rest of the view.
            center={[pin.latitude, normalizeLongitude(pin.longitude, bounds)]}
            radius={pin.approximate ? 6 : 4}
            pathOptions={{
              color: '#ffffff',
              fillColor: '#0ea5e9',
              fillOpacity: pin.approximate ? 0.4 : 1,
              weight: 1.5,
            }}
          />
        )}
      </MapContainer>
    </div>
  );

  return (
    <div className={clsx('flex flex-col gap-0.5 flex-shrink-0', className)}>
      {pin ? (
        <a
          href={
            pin.approximate
              ? `https://www.openstreetmap.org/#map=${linkZoom}/${pin.latitude}/${pin.longitude}`
              : `https://www.openstreetmap.org/?mlat=${pin.latitude}&mlon=${pin.longitude}#map=${linkZoom}/${pin.latitude}/${pin.longitude}`
          }
          target="_blank"
          rel="noopener noreferrer"
          title={linkTitle}
          aria-label={
            pin.approximate
              ? `Approximate location: ${countryName}, open in OpenStreetMap`
              : label
                ? `Map of ${label}, open in OpenStreetMap`
                : 'Open location in OpenStreetMap'
          }
          onClick={(e) => e.stopPropagation()}
        >
          {map}
        </a>
      ) : (
        <div className="relative" title="No location on file" aria-label="No location on file">
          {map}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span className="text-[10px] text-dark-text-muted text-center px-1">No location</span>
          </div>
        </div>
      )}
      {/* Attribution is required by the OSM tile policy, and attributionControl is off */}
      <span className="text-[9px] leading-none text-dark-text-muted text-right">© OpenStreetMap</span>
    </div>
  );
}
